import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  Radio, 
  Zap, 
  Monitor, 
  Smartphone, 
  MapPin, 
  Play, 
  BarChart3,
  Eye,
  MousePointer,
  Target,
  RefreshCw,
  Send
} from "lucide-react";

const tierColors = {
  GOLD: "tier-gold border",
  SILVER: "tier-silver border",
  BRONZE: "tier-bronze border",
  HOUSE: "bg-secondary text-secondary-foreground"
};

export default function AdServing() {
  const { token, isAdmin } = useAuth();
  const [adServingStats, setAdServingStats] = useState(null);
  const [houseAds, setHouseAds] = useState([]);
  const [testResult, setTestResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  
  // Test form state
  const [testForm, setTestForm] = useState({
    trip_id: `trip-${Date.now()}`,
    passenger_id: "test-passenger",
    driver_id: "test-driver",
    panel_id: "test-panel",
    current_lat: -33.8688,
    current_lng: 151.2093,
    pickup_lat: -33.8600,
    pickup_lng: 151.2000,
    destination_lat: -33.8700,
    destination_lng: 151.2100,
    surface: "PANEL"
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statsRes, houseAdsRes] = await Promise.all([
        isAdmin ? axios.get(`${API}/ad-serving/stats`, { headers }) : Promise.resolve({ data: {} }),
        axios.get(`${API}/ad-serving/house-ads`, { headers })
      ]);
      
      setAdServingStats(statsRes.data);
      setHouseAds(houseAdsRes.data.house_ads || []);
    } catch (error) {
      console.error("Failed to fetch ad serving data:", error);
    } finally {
      setLoading(false);
    }
  };

  const testInventory = async () => {
    setTesting(true);
    try {
      const response = await axios.post(
        `${API}/ad-serving/inventory?surface=${testForm.surface}`,
        {
          trip_id: testForm.trip_id,
          passenger_id: testForm.passenger_id,
          driver_id: testForm.driver_id,
          panel_id: testForm.panel_id,
          current_lat: testForm.current_lat,
          current_lng: testForm.current_lng,
          pickup_lat: testForm.pickup_lat,
          pickup_lng: testForm.pickup_lng,
          destination_lat: testForm.destination_lat,
          destination_lng: testForm.destination_lng
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTestResult(response.data);
      toast.success(`Retrieved ${response.data.total_ads} ads for ${testForm.surface}`);
    } catch (error) {
      toast.error("Failed to test inventory");
      console.error(error);
    } finally {
      setTesting(false);
    }
  };

  const simulateExposure = async (ad) => {
    try {
      await axios.post(`${API}/ad-serving/events/exposure`, {
        trip_id: testForm.trip_id,
        passenger_id: testForm.passenger_id,
        panel_id: testForm.panel_id,
        ad_id: ad.id,
        campaign_id: ad.campaign_id,
        creative_id: ad.creative.creative_id,
        account_id: ad.account_id,
        surface: testForm.surface,
        visible_ms: 2500,
        visible_percent: 100
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Exposure event recorded (billable)");
    } catch (error) {
      toast.error("Failed to record exposure");
    }
  };

  const simulateClick = async (ad) => {
    try {
      await axios.post(`${API}/ad-serving/events/engagement`, {
        trip_id: testForm.trip_id,
        passenger_id: testForm.passenger_id,
        ad_id: ad.id,
        campaign_id: ad.campaign_id,
        creative_id: ad.creative.creative_id,
        account_id: ad.account_id,
        event_type: "CLICK",
        surface: testForm.surface
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Click event recorded");
    } catch (error) {
      toast.error("Failed to record click");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-pulse text-primary">Loading ad serving data...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="ad-serving-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black tracking-tight flex items-center gap-3">
            <Radio className="h-10 w-10 text-primary" />
            Ad Serving Engine
          </h1>
          <p className="text-muted-foreground mt-1">
            Real-time ad delivery with geo-perimeter targeting
          </p>
        </div>
        <Button onClick={fetchData} variant="outline" data-testid="refresh-btn">
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Admin Stats */}
      {isAdmin && adServingStats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Today's Impressions</p>
                  <p className="text-2xl font-bold mt-1">{adServingStats.today?.total_impressions || 0}</p>
                </div>
                <Eye className="h-6 w-6 text-primary/60" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Billable</p>
                  <p className="text-2xl font-bold mt-1">{adServingStats.today?.total_billable || 0}</p>
                </div>
                <Zap className="h-6 w-6 text-green-500/60" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Clicks</p>
                  <p className="text-2xl font-bold mt-1">{adServingStats.today?.total_clicks || 0}</p>
                </div>
                <MousePointer className="h-6 w-6 text-yellow-500/60" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Delivered Customers</p>
                  <p className="text-2xl font-bold mt-1">{adServingStats.today?.total_cpdc || 0}</p>
                </div>
                <Target className="h-6 w-6 text-purple-500/60" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Active Campaigns</p>
                  <p className="text-2xl font-bold mt-1">{adServingStats.active_campaigns || 0}</p>
                </div>
                <BarChart3 className="h-6 w-6 text-muted-foreground/60" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs defaultValue="test" className="space-y-6">
        <TabsList>
          <TabsTrigger value="test" data-testid="test-tab">Inventory Tester</TabsTrigger>
          <TabsTrigger value="house-ads" data-testid="house-ads-tab">House Ads</TabsTrigger>
          <TabsTrigger value="api" data-testid="api-tab">API Reference</TabsTrigger>
        </TabsList>

        {/* Inventory Tester */}
        <TabsContent value="test" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Test Form */}
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Play className="h-5 w-5 text-primary" />
                  Test Ad Inventory
                </CardTitle>
                <CardDescription>
                  Simulate a trip to see which ads would be served
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-2">
                  {["PANEL", "PASSENGER_APP", "DRIVER_APP"].map(surface => (
                    <Button
                      key={surface}
                      variant={testForm.surface === surface ? "default" : "outline"}
                      size="sm"
                      onClick={() => setTestForm(prev => ({ ...prev, surface }))}
                      className="w-full"
                    >
                      {surface === "PANEL" && <Monitor className="mr-1 h-4 w-4" />}
                      {surface !== "PANEL" && <Smartphone className="mr-1 h-4 w-4" />}
                      {surface.replace("_", " ")}
                    </Button>
                  ))}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Current Latitude</Label>
                    <Input
                      type="number"
                      step="any"
                      value={testForm.current_lat}
                      onChange={e => setTestForm(prev => ({ ...prev, current_lat: parseFloat(e.target.value) }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Current Longitude</Label>
                    <Input
                      type="number"
                      step="any"
                      value={testForm.current_lng}
                      onChange={e => setTestForm(prev => ({ ...prev, current_lng: parseFloat(e.target.value) }))}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Destination Latitude</Label>
                    <Input
                      type="number"
                      step="any"
                      value={testForm.destination_lat}
                      onChange={e => setTestForm(prev => ({ ...prev, destination_lat: parseFloat(e.target.value) }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Destination Longitude</Label>
                    <Input
                      type="number"
                      step="any"
                      value={testForm.destination_lng}
                      onChange={e => setTestForm(prev => ({ ...prev, destination_lng: parseFloat(e.target.value) }))}
                    />
                  </div>
                </div>

                <div className="p-3 rounded-lg bg-secondary/30 text-sm">
                  <p className="text-muted-foreground mb-2">Quick Locations (Australia):</p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { name: "Sydney CBD", lat: -33.8688, lng: 151.2093 },
                      { name: "Melbourne", lat: -37.8136, lng: 144.9631 },
                      { name: "Brisbane", lat: -27.4705, lng: 153.026 },
                      { name: "Perth", lat: -31.9505, lng: 115.8605 },
                      { name: "Adelaide", lat: -34.9285, lng: 138.6007 }
                    ].map(loc => (
                      <Button
                        key={loc.name}
                        variant="outline"
                        size="sm"
                        onClick={() => setTestForm(prev => ({
                          ...prev,
                          current_lat: loc.lat,
                          current_lng: loc.lng,
                          destination_lat: loc.lat + 0.01,
                          destination_lng: loc.lng + 0.01
                        }))}
                      >
                        <MapPin className="mr-1 h-3 w-3" />
                        {loc.name}
                      </Button>
                    ))}
                  </div>
                </div>

                <Button 
                  onClick={testInventory} 
                  className="w-full" 
                  disabled={testing}
                  data-testid="test-inventory-btn"
                >
                  {testing ? (
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="mr-2 h-4 w-4" />
                  )}
                  Get Ad Inventory
                </Button>
              </CardContent>
            </Card>

            {/* Test Results */}
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle>Inventory Results</CardTitle>
                <CardDescription>
                  {testResult 
                    ? `${testResult.total_ads} ads for ${testResult.surface}` 
                    : "Run a test to see results"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {testResult ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sm">
                      <Badge variant={testResult.has_paid_inventory ? "default" : "secondary"}>
                        {testResult.has_paid_inventory ? "Paid Inventory" : "House Ads Only"}
                      </Badge>
                      <span className="text-muted-foreground">
                        Trip: {testResult.trip_id}
                      </span>
                    </div>

                    <div className="space-y-3">
                      {testResult.inventory.map((ad, idx) => (
                        <div 
                          key={ad.id} 
                          className="p-4 rounded-lg border border-border bg-secondary/20"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <div className="flex items-center gap-2">
                                <h4 className="font-semibold">{ad.business_name}</h4>
                                <Badge className={tierColors[ad.tier]}>{ad.tier}</Badge>
                              </div>
                              <p className="text-sm text-muted-foreground">
                                {ad.creative.name} • Priority: {ad.priority_score}
                              </p>
                              {ad.distance_meters && (
                                <p className="text-xs text-muted-foreground">
                                  {ad.distance_meters}m from location
                                </p>
                              )}
                            </div>
                            <div className="text-right text-xs text-muted-foreground">
                              <p>CPM: ${ad.cpm_rate}</p>
                              {ad.cpc_rate && <p>CPC: ${ad.cpc_rate}</p>}
                              {ad.cpdc_rate && <p>CPDC: ${ad.cpdc_rate}</p>}
                            </div>
                          </div>
                          
                          <div className="aspect-video bg-secondary rounded overflow-hidden mb-3">
                            <img 
                              src={ad.creative.file_url} 
                              alt={ad.creative.name}
                              className="w-full h-full object-cover"
                              onError={e => { e.target.style.background = '#27272A'; e.target.alt = 'Preview'; }}
                            />
                          </div>

                          {ad.tier !== "HOUSE" && (
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="outline" 
                                onClick={() => simulateExposure(ad)}
                              >
                                <Eye className="mr-1 h-3 w-3" />
                                Simulate View
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline" 
                                onClick={() => simulateClick(ad)}
                              >
                                <MousePointer className="mr-1 h-3 w-3" />
                                Simulate Click
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Radio className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No results yet</p>
                    <p className="text-sm">Configure a trip and click "Get Ad Inventory"</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* House Ads */}
        <TabsContent value="house-ads" className="space-y-6">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle>House Ads (Fallback)</CardTitle>
              <CardDescription>
                Booma promotional content shown when no paid inventory matches
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {houseAds.map(ad => (
                  <div 
                    key={ad.id} 
                    className="rounded-lg border border-border overflow-hidden"
                  >
                    <div className="aspect-video bg-secondary">
                      <img 
                        src={ad.file_url} 
                        alt={ad.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="font-semibold">{ad.name}</h4>
                      <p className="text-sm text-muted-foreground">{ad.cta_text}</p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {ad.surfaces.map(s => (
                          <Badge key={s} variant="outline" className="text-xs">
                            {s.replace("_", " ")}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* API Reference */}
        <TabsContent value="api" className="space-y-6">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle>Ad Serving API Endpoints</CardTitle>
              <CardDescription>
                Integration reference for Passenger App, Driver App, and Drive Panels
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Inventory Endpoint */}
              <div className="p-4 rounded-lg bg-secondary/20 border border-border">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="bg-green-500">POST</Badge>
                  <code className="text-sm font-mono">/api/ad-serving/inventory?surface=PANEL</code>
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  Get ad inventory for a trip. Returns ads sorted by tier priority.
                </p>
                <pre className="text-xs bg-background p-3 rounded overflow-x-auto">
{`{
  "trip_id": "trip-123",
  "passenger_id": "pax-123",
  "driver_id": "drv-123",
  "panel_id": "panel-123",
  "current_lat": -33.8688,
  "current_lng": 151.2093,
  "pickup_lat": -33.8600,
  "pickup_lng": 151.2000,
  "destination_lat": -33.8700,
  "destination_lng": 151.2100
}`}
                </pre>
              </div>

              {/* Panel Inventory */}
              <div className="p-4 rounded-lg bg-secondary/20 border border-border">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="bg-green-500">POST</Badge>
                  <code className="text-sm font-mono">/api/ad-serving/inventory/panel</code>
                </div>
                <p className="text-sm text-muted-foreground">
                  Get exactly 4 ads for Drive Panel 4-tile layout. Fills with house ads if needed.
                </p>
              </div>

              {/* Events */}
              <div className="p-4 rounded-lg bg-secondary/20 border border-border">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="bg-green-500">POST</Badge>
                  <code className="text-sm font-mono">/api/ad-serving/events</code>
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  Batch ingest exposure and engagement events.
                </p>
                <pre className="text-xs bg-background p-3 rounded overflow-x-auto">
{`{
  "exposures": [{
    "trip_id": "trip-123",
    "ad_id": "ad-123",
    "campaign_id": "camp-123",
    "visible_ms": 2000,
    "visible_percent": 100,
    "surface": "PANEL"
  }],
  "engagements": [{
    "trip_id": "trip-123",
    "ad_id": "ad-123",
    "event_type": "CLICK",
    "surface": "PANEL"
  }],
  "device_id": "panel-123",
  "app_version": "1.0.0"
}`}
                </pre>
              </div>

              {/* CPDC */}
              <div className="p-4 rounded-lg bg-secondary/20 border border-border">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="bg-green-500">POST</Badge>
                  <code className="text-sm font-mono">/api/ad-serving/events/cpdc</code>
                </div>
                <p className="text-sm text-muted-foreground">
                  Record Cost Per Delivered Customer event when passenger is dropped at advertiser location.
                </p>
              </div>

              {/* Billable Rules */}
              <div className="p-4 rounded-lg border border-primary/30 bg-primary/5">
                <h4 className="font-semibold mb-2">Billable Impression Rules</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Minimum visibility: <strong>50%</strong> of ad visible</li>
                  <li>• Minimum duration: <strong>1.5 seconds</strong> (1500ms)</li>
                  <li>• Both conditions must be met for Views billing</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
